import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Popup, GeoJSON, useMap } from 'react-leaflet';
import AssignMarkerIcon from '../../ListingStatusIcon';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import client from '../../../lib/feathers';
import { formatAddressDisplay } from '../../../lib/addressUtils';

/**
 * Child component that re-centers the map when coordinates change.
 * MapContainer's `center` prop is immutable after mount in react-leaflet.
 */
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

const APPELLATION_STYLE = {
  color: '#630801',
  weight: 2,
  fillColor: 'rgba(99, 8, 1, 0.15)',
  fillOpacity: 0.15,
};

export default function LocationInfoPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const [homeMarker, setHomeMarker] = useState();
  const [boundaries, setBoundaries] = useState([]);

  const isHosted = !!listing.hostListingId;
  const hostListing = listing.hostListing;
  const displayAddress = isHosted && hostListing?.address ? hostListing.address : listing.address;
  // Only consider a location "displayable" if it actually has a 2-element
  // coordinates array. PostGIS columns sometimes hydrate as `{ crs, type }`
  // with no coordinates payload (older rows or rows imported via SQL), and
  // a truthy-but-empty object was crashing the panel at `coordinates[1]`.
  const rawLocation = isHosted && hostListing?.location ? hostListing.location : listing.location;
  const displayLocation = rawLocation && Array.isArray(rawLocation.coordinates) && rawLocation.coordinates.length >= 2
    ? rawLocation
    : null;

  useEffect(() => {
    if (displayLocation) {
      setHomeMarker({
        lat: displayLocation.coordinates[1],
        lon: displayLocation.coordinates[0],
      });
    }
  }, [displayLocation]);

  // Load appellation boundaries for the map
  useEffect(() => {
    if (!listing.appellations || listing.appellations.length === 0) return;
    const loadBoundaries = async () => {
      try {
        const service = client.service(`${user?.role === 'industry' ? 'industry' : 'admin'}/appellation`);
        const ids = listing.appellations.map(a => a.id);
        const result = await service.find({
          query: { id: { $in: ids }, $limit: ids.length }
        });
        const withBoundary = result.data.filter(a => a.boundary);
        setBoundaries(withBoundary);
      } catch {
        // Boundaries are optional, fail silently
      }
    };
    loadBoundaries();
  }, [listing.appellations, user?.role]);

  const appellations = listing.appellations || [];
  // Sort most specific (deepest) first
  const sortedAppellations = [...appellations].sort((a, b) => (b.depth || 0) - (a.depth || 0));
  const basePath = user?.role === 'admin' ? '/admin' : '/industry';

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg px-4 py-5 sm:p-6">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Location
        </h3>
      </div>

      {/* Appellations */}
      {sortedAppellations.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Wine Appellations
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {sortedAppellations.map(a => (
                    user?.role === 'admin' ? (
                      <Link
                        key={a.id}
                        to={`${basePath}/appellations/${a.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200"
                      >
                        {a.name}
                        <span className="text-blue-500 uppercase">{a.designationType}</span>
                      </Link>
                    ) : (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
                      >
                        {a.name}
                        <span className="text-blue-500 uppercase">{a.designationType}</span>
                      </span>
                    )
                  ))}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Hosted notice */}
      {isHosted && hostListing && (
        <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
          <div className="rounded-md bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              This listing is located at{' '}
              <Link
                to={`${basePath}/listings/${hostListing.id}/`}
                className="font-medium text-blue-800 hover:text-blue-900 underline"
              >
                {hostListing.name}
              </Link>
              . The address and map below show the host venue's location.
            </p>
          </div>
        </div>
      )}

      {/* Address */}
      <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
        <dl className="sm:divide-y sm:divide-gray-200">
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">
              Address
            </dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {displayAddress ? (
                <div>
                  {displayAddress.address1}
                  {displayAddress.address2 && <><br />{displayAddress.address2}</>}
                  <br />
                  {formatAddressDisplay(displayAddress)}
                </div>
              ) : (
                <div className="text-gray-400">Not entered</div>
              )}
            </dd>
          </div>
          {displayLocation && (
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Coordinates
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {displayLocation.coordinates[1]}, {displayLocation.coordinates[0]}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Map */}
      <div className="relative h-96 mt-4 rounded-md overflow-hidden border border-gray-200">
        {homeMarker && (
          <MapContainer center={[homeMarker.lat, homeMarker.lon]} zoom={11} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
            <ChangeView center={[homeMarker.lat, homeMarker.lon]} zoom={11} />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {boundaries.map(a => (
              <GeoJSON
                key={a.id}
                data={a.boundary}
                style={APPELLATION_STYLE}
                onEachFeature={(feature, layer) => {
                  layer.bindTooltip(a.name, { sticky: true, className: 'text-xs' });
                }}
              />
            ))}
            <AssignMarkerIcon listingStatus={isHosted ? hostListing?.status : listing.status} position={[homeMarker.lat, homeMarker.lon]}>
              <Popup>{isHosted && hostListing ? `${listing.name} (at ${hostListing.name})` : listing.name}</Popup>
            </AssignMarkerIcon>
          </MapContainer>
        )}
      </div>
    </div>
  );
}
